/**
 * Per-section error boundary for the console — one malformed payload (or
 * any render-time throw) degrades ONLY its card, never the whole page.
 *
 * The fallback stays inside the console design language: a bordered card,
 * a plain-language message, the technical detail in mono, and a retry
 * button that re-mounts the section (children get a fresh render — with
 * react-query still holding the data, a plain re-render is enough to
 * recover from transient issues).
 */

import { Trans } from "@lingui/react/macro";
import { CircleAlert, RotateCcw } from "lucide-react";
import * as React from "react";

import { ConButton } from "./atoms";

interface ConsoleSectionBoundaryProps {
  children: React.ReactNode;
  /** Optional section name shown in the fallback ("Job recenti", …). */
  name?: React.ReactNode;
}

interface ConsoleSectionBoundaryState {
  error: Error | null;
}

export class ConsoleSectionBoundary extends React.Component<
  ConsoleSectionBoundaryProps,
  ConsoleSectionBoundaryState
> {
  override state: ConsoleSectionBoundaryState = { error: null };

  static getDerivedStateFromError(error: Error): ConsoleSectionBoundaryState {
    return { error };
  }

  override componentDidCatch(error: Error, info: React.ErrorInfo): void {
    // Diagnostic breadcrumb — the payload/render mismatch that tripped the
    // boundary is exactly what we need in the console logs to fix drift.
    console.error("[console] section render failed:", error, info.componentStack);
  }

  private readonly reset = (): void => {
    this.setState({ error: null });
  };

  override render(): React.ReactNode {
    if (this.state.error === null) return this.props.children;
    return (
      <div
        role="alert"
        className="con-panel flex flex-col items-start gap-2 rounded-[16px] border border-[color:var(--con-border)] bg-[color:var(--con-surface-raised)] px-4 py-4"
      >
        <div className="flex items-center gap-2">
          <CircleAlert
            aria-hidden
            size={15}
            strokeWidth={1.75}
            className="shrink-0 text-[color:var(--con-danger-text)]"
          />
          <p className="font-medium text-[length:var(--con-text-sm)] text-[color:var(--con-on-surface)]">
            {this.props.name ? (
              <Trans>Impossibile mostrare la sezione «{this.props.name}»</Trans>
            ) : (
              <Trans>Impossibile mostrare questa sezione</Trans>
            )}
          </p>
        </div>
        <p className="text-[length:var(--con-text-xs)] text-[color:var(--con-on-surface-faint)]">
          <Trans>
            I dati ricevuti non sono nel formato atteso. Il resto della pagina continua a
            funzionare.
          </Trans>
        </p>
        <p className="max-w-full truncate font-mono text-[length:var(--con-text-2xs)] text-[color:var(--con-on-surface-faint)]">
          {this.state.error.message}
        </p>
        <ConButton size="sm" variant="outline" onClick={this.reset}>
          <RotateCcw aria-hidden strokeWidth={1.75} />
          <Trans>Riprova</Trans>
        </ConButton>
      </div>
    );
  }
}
